import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

const C = 299792458;

export default function RelativisticEnergyCalculator() {
  const [mass, setMass] = useState("");
  const [velocity, setVelocity] = useState("");
  const [results, setResults] = useState<any>(null);

  const calculateEnergy = () => {
    const m = parseFloat(mass);
    const v = parseFloat(velocity);

    if (isNaN(m) || isNaN(v)) {
      toast.error("Please enter valid values");
      return;
    }

    if (v >= C) {
      toast.error("Velocity must be less than speed of light");
      return;
    }

    const beta = v / C;
    const gamma = 1 / Math.sqrt(1 - beta * beta);
    
    const restEnergy = m * C * C;
    const totalEnergy = gamma * restEnergy;
    const kineticEnergy = totalEnergy - restEnergy;
    const momentum = gamma * m * v;

    setResults({
      gamma,
      restEnergy,
      totalEnergy,
      kineticEnergy,
      momentum,
    });
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setMass("");
    setVelocity("");
    setResults(null);
  };

  return (
    <PageLayout calculatorName="Relativistic Energy Calculator" calculatorPath="/calculator/relativistic-energy">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Relativistic Energy Calculator</h1>
          <p className="text-muted-foreground">
            Calculate relativistic energy and momentum
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>E = γmc² | p = γmv</CardTitle>
            <CardDescription>
              Energy and momentum at relativistic speeds
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mass">Mass (kg)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="any"
                  placeholder="Enter mass (e.g., 9.11e-31 for electron)"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="velocity">Velocity (m/s)</Label>
                <Input
                  id="velocity"
                  type="number"
                  step="any"
                  placeholder="Speed of object"
                  value={velocity}
                  onChange={(e) => setVelocity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  c = 299,792,458 m/s
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateEnergy} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {results && (
              <>
                <div className="space-y-3">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardContent className="pt-6 space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Lorentz Factor (γ)</p>
                        <p className="text-2xl font-bold text-primary">{results.gamma.toFixed(6)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Rest Energy (E₀)</p>
                        <p className="text-2xl font-bold text-primary">{results.restEnergy.toExponential(4)} J</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Energy (E)</p>
                        <p className="text-2xl font-bold text-primary">{results.totalEnergy.toExponential(4)} J</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Kinetic Energy (K)</p>
                        <p className="text-2xl font-bold text-primary">{results.kineticEnergy.toExponential(4)} J</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Relativistic Momentum</p>
                        <p className="text-2xl font-bold text-primary">{results.momentum.toExponential(4)} kg·m/s</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["relativistic-energy"]} trigger={results.totalEnergy} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• E = mc² gives rest energy</p>
                <p>• Total energy E = γmc² increases with velocity</p>
                <p>• Kinetic energy K = (γ-1)mc²</p>
                <p>• At low speeds: K ≈ ½mv² (classical)</p>
                <p>• Momentum increases without bound as v → c</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
